/**
 * Modal de câmera para fotografar a prescrição (pop-up "tirar foto", não anexo).
 *
 * Mostra o preview ao vivo da câmera (traseira no celular) e, ao tocar em
 * "Capturar", congela o frame num canvas → JPEG → devolve como File pro OCR.
 * Funciona igual no desktop (webcam) e no celular — sem depender do diálogo
 * de arquivo do sistema operacional.
 */
import { useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  onCapture: (file: File) => void;
  onClose: () => void;
}

export function PrescricaoCamera({ open, onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let stream: MediaStream | null = null;
    setErro(null);
    setPronto(false);

    async function start() {
      const video = videoRef.current;
      if (!video) return;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        video.srcObject = stream;
        await video.play();
        setPronto(true);
      } catch (e) {
        const err = e as Error;
        setErro(
          err.name === "NotAllowedError"
            ? "Permissão de câmera negada. Conceda acesso e tente de novo."
            : err.name === "NotFoundError"
              ? "Nenhuma câmera disponível neste dispositivo."
              : `Erro ao abrir câmera: ${err.message}`,
        );
      }
    }

    start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open]);

  function capturar() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(new File([blob], `prescricao-${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display text-lg font-semibold">Foto da prescrição</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md bg-black">
          <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-[88%] w-[86%] rounded-md border-2 border-white/80" />
          </div>
        </div>
        {erro ? (
          <p className="mt-2 text-sm text-anarosa">⚠️ {erro}</p>
        ) : (
          <p className="mt-2 text-xs text-ink-muted">
            Encaixe a receita inteira na moldura, com boa luz, e toque em Capturar.
          </p>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!pronto} onClick={capturar}>📷 Capturar</button>
        </div>
      </div>
    </div>
  );
}
